/**
 * Calculate the count of visible text characters in an HTML string,
 * treating HTML entities as single characters and skipping all HTML tags.
 *
 * @param {string} html - Raw HTML input string.
 * @returns {number} The count of visible characters.
 */
export function getVisibleTextLength(html) {
  let count = 0;
  let inTag = false;
  let i = 0;
  while (i < html.length) {
    if (html[i] === '<') {
      inTag = true;
    } else if (html[i] === '>') {
      inTag = false;
    } else if (!inTag) {
      if (html[i] === '&') {
        while (i < html.length && html[i] !== ';') {
          i++;
        }
      }
      count++;
    }
    i++;
  }
  return count;
}

/**
 * Safely slice an HTML string to include only a specific number of visible text
 * characters, while keeping all tag hierarchies closed and syntactically valid.
 *
 * @param {string} html - Raw HTML input string.
 * @param {number} maxVisibleChars - Maximum visible text character count to preserve.
 * @returns {string} Sliced HTML with all unclosed tags closed.
 */
export function sliceHtml(html, maxVisibleChars) {
  let result = '';
  let visibleCount = 0;
  let inTag = false;
  const tagStack = [];
  let i = 0;

  while (i < html.length && visibleCount < maxVisibleChars) {
    const char = html[i];

    if (char === '<') {
      inTag = true;
      let tagContent = '';
      while (i < html.length && html[i] !== '>') {
        tagContent += html[i];
        i++;
      }
      if (i < html.length) {
        tagContent += '>';
      }
      result += tagContent;

      const match = tagContent.match(/^<\/?([a-zA-Z0-9:-]+)/);
      if (match) {
        const tagName = match[1];
        const isClosing = tagContent.startsWith('</');
        const isSelfClosing = tagContent.endsWith('/>') || ['br', 'img', 'hr', 'input'].includes(tagName.toLowerCase());

        if (isClosing) {
          tagStack.pop();
        } else if (!isSelfClosing) {
          tagStack.push(tagName);
        }
      }
    } else {
      result += char;
      if (char === '&') {
        let entity = '';
        while (i < html.length && html[i] !== ';') {
          entity += html[i];
          i++;
        }
        if (i < html.length) {
          entity += ';';
        }
        result += entity.substring(1);
        visibleCount++;
      } else {
        visibleCount++;
      }
    }
    i++;
  }

  // Close any tags left open due to early termination of slicing
  while (tagStack.length > 0) {
    const tag = tagStack.pop();
    result += `</${tag}>`;
  }

  return result;
}
